import { useMemo, useRef, useState } from "react";
import Select, {
  GroupBase,
  MenuListProps,
  MenuProps,
  MultiValue,
  SelectInstance,
  SingleValue,
  StylesConfig,
} from "react-select";
import cn from "classnames";
import { useMobile } from "hooks/useMobile.hook";
import { useOnClickOutside } from "hooks/useOnClickOutside.hook";
import { AppDOM } from "utils/app.utils";
import { OptionType, SelectProps, SelectValue } from "./CustomSelect.types";
import {
  CustomDropdownIndicator,
  CustomInput,
  CustomLazyLoadedMenuList,
  CustomMenu,
  CustomMenuList,
  CustomMultiValue,
  CustomOption,
  CustomPlaceholder,
  CustomSingleValue,
} from "./CustomSelectComponents";
import s from "./CustomSelect.module.scss";

import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";

export const CustomSelect = <CustomOptionType, IsMulti extends boolean = false>({
  options,
  className,
  isMulti,
  componentsClassNames,
  handleChange,
  menuPlacement = "auto",
  isDropDownMenuRelative = false,
  isInputTypingEnabled = false,
  selectedIndicator = "checkmark",
  placeholderDataTestId,
  lazyLoadedMenuList = false,
  lazyLoadedMenuListRowHeight,
  styles: selectStyles,
  ...rest
}: SelectProps<CustomOptionType, IsMulti>) => {
  const isMobile = useMobile();
  const selectRef = useRef<SelectInstance<OptionType, boolean, GroupBase<OptionType>>>(null);
  const selectContainerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [touchStartTime, setTouchStartTime] = useState<number>(0);
  const useMenuPortal = !isDropDownMenuRelative;

  // Create cache only once to prevent DOM elements accumulation
  const emotionCache = useMemo(
    () =>
      createCache({
        container: AppDOM.appContainer,
        key: "react-select-styles",
        prepend: false,
      }),
    []
  );

  useOnClickOutside({
    ref: selectContainerRef,
    shadowRoot: AppDOM.shadowRootNode,
    excludeRefs: [menuRef],
    handler: () => {
      if (isMobile) {
        setIsOpen(false);
      }
    },
  });

  const handleTouchStart = () => {
    setTouchStartTime(Date.now());
  };

  const handleTouchEnd = () => {
    const touchDuration = Date.now() - touchStartTime;
    if (touchDuration < 500) {
      setIsOpen((prev) => !prev);
    }
  };

  const handleUpdateState = (value: MultiValue<OptionType> | SingleValue<OptionType>) => {
    handleChange(value as SelectValue<CustomOptionType, IsMulti>);

    setTimeout(() => {
      if (selectRef.current) {
        selectRef.current.focus();
      }
    }, 0);
  };

  const mergedStyles: StylesConfig<OptionType, boolean, GroupBase<OptionType>> = {
    ...selectStyles,
    menu: (base, props) => {
      const fromProps = selectStyles?.menu;
      const resolved = typeof fromProps === "function" ? fromProps(base, props) : fromProps;

      if (!useMenuPortal) {
        return { ...base, ...resolved };
      }

      return {
        ...base,
        position: "static",
        top: "auto",
        left: "auto",
        width: "100%",
        marginTop: 0,
        marginBottom: 0,
        ...resolved,
      };
    },
  };

  return (
    <div
      ref={selectContainerRef}
      onClick={() => {
        setIsOpen((prev) => !prev);
      }}
      onTouchStart={() => {
        if (isMobile) {
          handleTouchStart();
        }
      }}
      onTouchEnd={() => {
        if (isMobile) {
          handleTouchEnd();
        }
      }}
      className={s.selectContainer}
    >
      <CacheProvider value={emotionCache}>
        <Select<OptionType, boolean, GroupBase<OptionType>>
          ref={selectRef}
          menuIsOpen={isMobile ? isOpen : undefined}
          className={cn(s.select, className)}
          components={{
            Menu: (props) => (
              <CustomMenu
                {...(props as MenuProps<OptionType, boolean, GroupBase<OptionType>>)}
                portal={useMenuPortal}
                controlRef={selectContainerRef}
                menuRef={menuRef}
                menuClassName={cn(s.menu, { [s.menuRelative]: isDropDownMenuRelative }, componentsClassNames?.menu)}
                menuPortalClassName={componentsClassNames?.menuPortal}
              />
            ),
            MenuList: (props) =>
              lazyLoadedMenuList ? (
                <CustomLazyLoadedMenuList
                  {...(props as MenuListProps<OptionType, boolean, GroupBase<OptionType>>)}
                  className={cn(props.className, componentsClassNames?.menuList)}
                  lazyLoadedMenuListRowHeight={lazyLoadedMenuListRowHeight}
                />
              ) : (
                <CustomMenuList
                  {...(props as MenuListProps<OptionType, boolean, GroupBase<OptionType>>)}
                  className={cn(props.className, componentsClassNames?.menuList)}
                />
              ),
            DropdownIndicator: (props) => (
              <CustomDropdownIndicator
                {...props}
                className={cn(props.className, componentsClassNames?.dropdownIndicator)}
              />
            ),
            Option: (props) => (
              <CustomOption
                {...props}
                className={cn(props.className, componentsClassNames?.option)}
                selectedIndicator={selectedIndicator}
              />
            ),
            SingleValue: (props) => (
              <CustomSingleValue {...props} className={cn(props.className, componentsClassNames?.singleValue)} />
            ),
            MultiValue: (props) => (
              <CustomMultiValue {...props} className={cn(props.className, componentsClassNames?.multiValue)} />
            ),
            Placeholder: (props) => (
              <CustomPlaceholder
                {...props}
                className={cn(props.className, componentsClassNames?.placeholder)}
                dataTestId={placeholderDataTestId}
              />
            ),
            Input: (props) => <CustomInput {...props} className={cn(props.className, componentsClassNames?.input)} />,
          }}
          blurInputOnSelect={!isMulti}
          classNamePrefix="react-select"
          options={options}
          isSearchable={isInputTypingEnabled}
          isMulti={Boolean(isMulti)}
          hideSelectedOptions={false}
          closeMenuOnSelect={!isMulti}
          onChange={handleUpdateState}
          {...rest}
          menuPlacement={menuPlacement}
          styles={mergedStyles}
          classNames={{
            menu: () => cn(s.menu, { [s.menuRelative]: isDropDownMenuRelative }, componentsClassNames?.menu),
          }}
        />
      </CacheProvider>
    </div>
  );
};
